// Icon mapping for player card customization
import React, { useId } from 'react';
import * as LucideIcons from 'lucide-react';
import { Icon as LucideIcon } from 'lucide-react';
import { LUCIDE_CATEGORY_MAP, LUCIDE_CATEGORY_ORDER } from '@/utils/lucideCategories';
import labIconNodes from '@lucide/lab/icon-nodes.json';

const LUCIDE_ICON_NAMES = Array.from(
  new Set(Object.values(LUCIDE_CATEGORY_MAP).flat())
);

const toPascalCase = (value) =>
  value
    .split('-')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ''))
    .join('');

const addKeysToIconNode = (name, iconNode) => {
  if (!Array.isArray(iconNode)) return iconNode;
  return iconNode.map((node, index) => {
    if (!Array.isArray(node)) return node;
    const [tag, attrs, children] = node;
    const nextAttrs = { ...(attrs || {}), key: `${name}-${index}` };
    if (!Array.isArray(children)) return [tag, nextAttrs];
    return [tag, nextAttrs, children];
  });
};

const LAB_ICON_MAP = Object.entries(labIconNodes).reduce((acc, [name, iconNode]) => {
  const pascalName = toPascalCase(name);
  if (LucideIcons[pascalName]) return acc;
  const keyedIconNode = addKeysToIconNode(name, iconNode);
  acc[pascalName] = (props) => <LucideIcon iconNode={keyedIconNode} {...props} />;
  return acc;
}, {});

const LAB_ICON_NAMES = Object.keys(LAB_ICON_MAP);

const SUPPORTER_HEART_GRADIENTS = {
  bronze: ['#f2b3a0', '#c96a4a', '#7b2d26'],
  silver: ['#f8fafc', '#cbd5e1', '#94a3b8'],
  gold: ['#fef3c7', '#fbbf24', '#b45309'],
};

const FilledHeartIcon = ({ size = 24, style = {}, tier = 'bronze', className = '' }) => {
  const gradientId = useId();
  const [start, mid, end] = SUPPORTER_HEART_GRADIENTS[tier] || SUPPORTER_HEART_GRADIENTS.bronze;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="4" y1="4" x2="20" y2="20" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={start} />
          <stop offset="55%" stopColor={mid} />
          <stop offset="100%" stopColor={end} />
        </linearGradient>
      </defs>
      <path
        d="m12 21.35-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54z"
        fill={`url(#${gradientId})`}
        stroke="rgba(255,255,255,0.22)"
        strokeWidth="0.75"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const SUPPORTER_HEART_ICONS = [
  {
    key: 'SupporterHeart',
    title: 'Heart',
    description: 'Any supporter',
    minPlanRank: 1,
    style: 'outline',
  },
  {
    key: 'SupporterHeartBronze',
    title: 'Bronze Heart',
    description: 'Bronze supporter',
    minPlanRank: 1,
    style: 'filled',
    tier: 'bronze',
  },
  {
    key: 'SupporterHeartSilver',
    title: 'Silver Heart',
    description: 'Silver supporter',
    minPlanRank: 2,
    style: 'filled',
    tier: 'silver',
  },
  {
    key: 'SupporterHeartGold',
    title: 'Gold Heart',
    description: 'Gold supporter',
    minPlanRank: 3,
    style: 'filled',
    tier: 'gold',
  },
];

const SUPPORTER_HEART_ICON_MAP = {
  SupporterHeart: LucideIcons.Heart,
  SupporterHeartBronze: (props) => <FilledHeartIcon {...props} tier="bronze" />,
  SupporterHeartSilver: (props) => <FilledHeartIcon {...props} tier="silver" />,
  SupporterHeartGold: (props) => <FilledHeartIcon {...props} tier="gold" />,
};

// Mapping of built-in Lucide icon names to components
export const ICON_MAP = LUCIDE_ICON_NAMES.reduce((acc, name) => {
  if (LucideIcons[name]) acc[name] = LucideIcons[name];
  return acc;
}, {});
Object.assign(ICON_MAP, LAB_ICON_MAP);
Object.assign(ICON_MAP, SUPPORTER_HEART_ICON_MAP);

const iconCategoryLookup = (() => {
  const map = {};
  LUCIDE_CATEGORY_ORDER.forEach((category) => {
    const icons = LUCIDE_CATEGORY_MAP[category.id] || [];
    icons.forEach((icon) => {
      if (!map[icon]) map[icon] = category.id;
    });
  });
  return map;
})();

const labIconNameSet = new Set(LAB_ICON_NAMES);

// Default built-in icons metadata (all unlocked by default)
export const DEFAULT_ICONS = Object.keys(ICON_MAP).map(name => ({
  id: name.toLowerCase(),
  name: name,
  type: 'lucide',
  locked: false,
  isBuiltIn: true,
  category: iconCategoryLookup[name] || (labIconNameSet.has(name) ? 'other' : 'regular')
}));

// Helper function to get an icon component by name (for built-in Lucide icons)
export const getIconByName = (iconName) => {
  return ICON_MAP[iconName] || null;
};

export const supportsFilledIconStyle = (iconName) => {
  const key = String(iconName || '').trim();
  if (!key) return false;
  return Boolean(ICON_MAP[key]) && !Object.prototype.hasOwnProperty.call(SUPPORTER_HEART_ICON_MAP, key);
};

// Get all available built-in icon names
export const getAvailableIconNames = () => {
  return Object.keys(ICON_MAP).sort();
};

// Render custom icon based on type

// Standardized thickness for all icons
const STANDARD_ICON_STROKE_WIDTH = 2;

export const renderNamedIcon = (
  iconName,
  size = 16,
  {
    strokeWidth = STANDARD_ICON_STROKE_WIDTH,
    style = {},
    className = '',
    filled = false,
  } = {}
) => {
  const IconComponent = getIconByName(iconName);
  if (!IconComponent) return null;
  const allowFilled = filled && supportsFilledIconStyle(iconName);
  if (!allowFilled) {
    return (
      <IconComponent
        size={size}
        strokeWidth={strokeWidth}
        style={style}
        className={className}
      />
    );
  }
  const filledLayerStyle = {
    ...style,
    position: 'absolute',
    inset: 0,
    fill: 'currentColor',
    fillOpacity: 0.38,
    paintOrder: 'stroke fill markers',
  };
  const outlineLayerStyle = {
    ...style,
    position: 'absolute',
    inset: 0,
  };
  return (
    <span
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
      }}
    >
      <IconComponent
        size={size}
        strokeWidth={strokeWidth}
        style={filledLayerStyle}
      />
      <IconComponent
        size={size}
        strokeWidth={strokeWidth}
        style={outlineLayerStyle}
      />
    </span>
  );
};

export const renderCustomIcon = (icon, size = 16, options = {}) => {
  if (!icon) return null;
  const {
    filled = false,
    strokeWidth = STANDARD_ICON_STROKE_WIDTH,
    ...style
  } = options || {};

  if (icon.type === 'lucide' || icon.isBuiltIn) {
    return renderNamedIcon(icon.name, size, { strokeWidth, style, filled });
  }

  // Custom uploaded icons
  const baseStyle = {
    width: size,
    height: size,
    display: 'inline-block',
    ...style
  };

  switch (icon.type) {
    case 'svg':
    case 'png':
    case 'gif':
      return <img src={icon.url} alt={icon.name} style={baseStyle} />;
    
    case 'lottie':
      // Placeholder for Lottie animation support (requires lottie-react library)
      return (
        <div style={baseStyle} title="Lottie animation support coming soon">
          {/* TODO: Implement Lottie player */}
          <img src={icon.url} alt={icon.name} style={baseStyle} />
        </div>
      );
    
    default:
      return null;
  }
};
